<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import { ReservationsService } from '@/api/services/reservations.service';
  import SectorsService from '@/api/services/sectors.service';
  import TablesService from '@/api/services/tables.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import V2ReservationFloorPlanPicker from '@/components/v2/reservations/settings/V2ReservationFloorPlanPicker.vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import {
    groupAreas,
    groupTableOptions,
    unplacedGroupTables,
  } from '@/domain/reservations/reservation-group-tables';
  import type { IReservationGroupTableOption } from '@/domain/reservations/reservation-group-tables';
  import {
    GROUP_MAX_TABLES,
    GROUP_MIN_TABLES,
  } from '@/domain/reservations/reservation-group-range';
  import { AlertType } from '@/interfaces/IAlert';
  import type {
    IReservationSettingsConfiguration,
    IReservationTableGroup,
  } from '@/interfaces/IReservationSettings';
  import type { ISector } from '@/interfaces/ISector';
  import type { ITable } from '@/interfaces/ITable';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const props = defineProps<{
    configuration: IReservationSettingsConfiguration;
    group?: IReservationTableGroup;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const modals = useModal();
  const ui = useUi();
  const store = useRestaurantStore();
  const { restaurantId, branchId } = props.configuration.settings;
  const modalName = 'v2-reservation-table-group';
  const name = ref(props.group?.name ?? '');
  const customName = ref(!!props.group);
  const tableIds = ref([...(props.group?.tableIds ?? [])]);
  const minParty = ref(String(props.group?.minParty ?? 2));
  const maxParty = ref(String(props.group?.maxParty ?? 6));
  // Until the owner types a range, "Hasta" follows the seats of the chosen tables.
  const customRange = ref(!!props.group);
  const submitted = ref(false);
  const contentRef = ref<HTMLElement | null>(null);
  const saving = ref(false);
  const error = ref('');
  let alive = true;
  onBeforeUnmount(() => {
    alive = false;
  });
  function current() {
    return (
      alive &&
      store.restaurant?.id === restaurantId &&
      store.selectedBranch?.id === branchId
    );
  }
  watch(
    () => [store.restaurant?.id, store.selectedBranch?.id],
    () => {
      if (!current()) modals.hide(modalName);
    }
  );
  const tr = (key: string) => String(proxy.$t(key));

  //#region Floor plan: sectors and table placement, loaded on open
  const sectors = ref<ISector[] | null>(null);
  const layout = ref<ITable[] | null>(null);
  const loading = ref(true);
  const loadFailed = ref(false);
  const activeSectorId = ref<number | null>(null);
  async function load() {
    loading.value = true;
    loadFailed.value = false;
    try {
      const [sectorList, tableList] = await Promise.all([
        SectorsService.getAll(restaurantId, branchId),
        TablesService.getAll(restaurantId, branchId),
      ]);
      if (!current()) return;
      sectors.value = sectorList;
      layout.value = tableList;
      // The tables card names sectors from the store; tables stay local.
      store.setSectors(sectorList);
      if (activeSectorId.value === null)
        activeSectorId.value = defaultSectorId();
      loading.value = false;
    } catch (cause) {
      if (!current()) return;
      loadFailed.value = true;
      loading.value = false;
      ui.serverError(cause, tr('reservations.groups.form.loadError'));
    }
  }
  onMounted(load);
  const options = computed(() =>
    groupTableOptions(props.configuration.tables, layout.value ?? [])
  );
  const areas = computed(() =>
    groupAreas(sectors.value ?? [], branchId, options.value)
  );
  const unplaced = computed(() =>
    unplacedGroupTables(areas.value, options.value)
  );
  // When editing, open on the area that holds the group's tables.
  function defaultSectorId(): number | null {
    const holding = areas.value.find((area) =>
      area.tables.some((table) => tableIds.value.includes(table.id))
    );
    return (holding ?? areas.value[0])?.sector.id ?? null;
  }
  const activeArea = computed(
    () =>
      areas.value.find((area) => area.sector.id === activeSectorId.value) ??
      areas.value[0] ??
      null
  );
  const areaItems = computed(() =>
    areas.value.map((area) => ({
      id: String(area.sector.id),
      label: area.sector.name,
    }))
  );
  /** The area a chosen table sits in, when it is not the one on screen. */
  function otherAreaName(table: IReservationGroupTableOption): string | null {
    const area = areas.value.find((candidate) =>
      candidate.tables.some((row) => row.id === table.id)
    );
    return area && area.sector.id !== activeArea.value?.sector.id
      ? area.sector.name
      : null;
  }
  //#endregion

  //#region Selection and validation
  const selected = computed(() =>
    options.value.filter((table) => tableIds.value.includes(table.id))
  );
  const full = computed(() => tableIds.value.length >= GROUP_MAX_TABLES);
  const capacity = computed(() =>
    selected.value.reduce((sum, table) => sum + table.capacity, 0)
  );
  const overCapacity = computed(() => Number(maxParty.value) > capacity.value);
  const seatless = computed(() =>
    selected.value.filter((table) => table.capacity === 0)
  );
  // Guests never get a combination with a disabled or staff-only member.
  const offlineSelected = computed(() =>
    selected.value.filter(
      (table) => !table.enabled || table.reservationsMode !== 'online'
    )
  );
  const tablesValid = computed(
    () =>
      selected.value.length === tableIds.value.length &&
      tableIds.value.length >= GROUP_MIN_TABLES &&
      tableIds.value.length <= GROUP_MAX_TABLES
  );
  const rangeValid = computed(
    () =>
      [minParty.value, maxParty.value].every(
        (value) =>
          /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 200
      ) && Number(minParty.value) <= Number(maxParty.value)
  );
  function toggleTable(id: number) {
    if (saving.value) return;
    const chosen = tableIds.value.includes(id);
    if (!chosen && full.value) return;
    tableIds.value = chosen
      ? tableIds.value.filter((value) => value !== id)
      : [...tableIds.value, id];
    if (!customName.value)
      name.value = selected.value
        .map((table) => table.name)
        .join(' + ')
        .slice(0, 64);
    if (!customRange.value && capacity.value > 0)
      maxParty.value = String(Math.min(capacity.value, 200));
  }
  function setName(value: string) {
    name.value = value;
    customName.value = value.trim().length > 0;
  }
  function setRange(bound: 'min' | 'max', value: string) {
    if (bound === 'min') minParty.value = value;
    else maxParty.value = value;
    customRange.value = true;
  }
  /** Brings the first message that explains why the form did not save into view. */
  async function revealProblem() {
    await nextTick();
    contentRef.value
      ?.querySelector('[role="alert"]')
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
  //#endregion

  async function save() {
    submitted.value = true;
    if (!current() || saving.value) return;
    if (
      !name.value.trim() ||
      !tablesValid.value ||
      !rangeValid.value ||
      overCapacity.value
    ) {
      await revealProblem();
      return;
    }
    saving.value = true;
    error.value = '';
    const dto = {
      restaurantId,
      branchId,
      name: name.value.trim(),
      tableIds: tableIds.value,
      minParty: Number(minParty.value),
      maxParty: Number(maxParty.value),
    };
    try {
      if (props.group)
        await ReservationsService.updateTableGroup(props.group.id, dto);
      else await ReservationsService.createTableGroup(dto);
      if (!current()) return;
      ui.showToast(AlertType.Success, tr('reservations.groups.saved'));
      proxy.$parent?.$emit('saved');
      modals.hide(modalName);
    } catch (cause) {
      if (!current()) return;
      saving.value = false;
      error.value = tr('reservations.groups.error');
      ui.serverError(cause, error.value);
    }
  }
</script>

<template>
  <form class="reservation-group-modal" @submit.prevent="save">
    <header class="modal-header">
      <h2>{{ $t('reservations.groups.form.title') }}</h2>
      <!-- Branches without a floor plan pick from a plain list instead. -->
      <p>
        {{
          $t(
            loading || areas.length
              ? 'reservations.groups.form.planHint'
              : 'reservations.groups.form.pickHint'
          )
        }}
      </p>
    </header>
    <div ref="contentRef" class="modal-content">
      <PosUiField
        :label="$t('reservations.groups.form.tables')"
        :error="
          submitted && !tablesValid
            ? $t('reservations.groups.form.tablesRequired')
            : undefined
        "
        required
      >
        <div v-if="loading" class="plan-loading" aria-busy="true">
          <PosUiSkeleton class="plan-skeleton" />
        </div>
        <div v-else-if="loadFailed" class="plan-error" role="alert">
          <span>{{ $t('reservations.groups.form.loadError') }}</span>
          <PosUiButton
            color="neutral"
            variant="outline"
            icon="refresh"
            @click="load"
            >{{ $t('reservations.page.retry') }}</PosUiButton
          >
        </div>
        <div v-else class="tables-picker">
          <PosUiSegmented
            v-if="areaItems.length > 1"
            class="area-switch"
            :value="String(activeArea?.sector.id ?? '')"
            :items="areaItems"
            block
            :aria-label="$t('reservations.groups.form.area')"
            @input="activeSectorId = Number($event)"
          />
          <V2ReservationFloorPlanPicker
            v-if="activeArea"
            :key="activeArea.sector.id"
            :sector="activeArea.sector"
            :tables="activeArea.tables"
            :selected-ids="tableIds"
            :full="full"
            :disabled="saving"
            @toggle="toggleTable"
          />
          <!-- Feedback sits right under the plan; the list of unplaced
               tables moves above it when it is the only picker. -->
          <div class="selection">
            <!-- The count line summarises the state for screen readers. -->
            <h3 aria-live="polite" aria-atomic="true">
              {{ $t('reservations.groups.form.selected') }}
              <span v-if="selected.length" class="selection-seats"
                >· {{ $t('reservations.timeline.seats', { capacity }) }}</span
              >
              <span v-if="full" class="selection-limit">{{
                $t('reservations.groups.form.maxTables', {
                  max: GROUP_MAX_TABLES,
                })
              }}</span>
            </h3>
            <p v-if="!selected.length" class="selection-empty">
              {{ $t('reservations.groups.form.noneSelected') }}
            </p>
            <ul v-else class="selection-list">
              <li
                v-for="table in selected"
                :key="table.id"
                class="selection-chip"
              >
                <span
                  >{{ table.name
                  }}<span v-if="otherAreaName(table)" class="selection-area">
                    · {{ otherAreaName(table) }}</span
                  ></span
                >
                <button
                  type="button"
                  class="selection-remove"
                  :aria-label="
                    $t('reservations.groups.form.remove', { table: table.name })
                  "
                  :disabled="saving"
                  @click="toggleTable(table.id)"
                >
                  <PosUiIcon name="close" :size="16" />
                </button>
              </li>
            </ul>
            <p
              v-for="table in offlineSelected"
              :key="`offline-${table.id}`"
              class="selection-warning"
            >
              {{
                $t('reservations.groups.form.notOnline', { table: table.name })
              }}
            </p>
            <p
              v-for="table in seatless"
              :key="`seatless-${table.id}`"
              class="selection-warning"
              role="alert"
            >
              {{
                $t('reservations.groups.form.noSeats', { table: table.name })
              }}
            </p>
            <p
              v-if="!seatless.length && tablesValid && overCapacity"
              class="selection-warning"
              role="alert"
            >
              {{ $t('reservations.groups.form.overCapacity') }} ·
              {{ $t('reservations.timeline.seats', { capacity }) }}
            </p>
          </div>
          <div
            v-if="unplaced.length"
            class="unplaced"
            :class="{ first: !areas.length }"
            role="group"
            :aria-labelledby="
              areas.length ? 'reservation-group-unplaced' : undefined
            "
            :aria-label="
              areas.length ? undefined : $t('reservations.groups.form.tables')
            "
          >
            <h3 v-if="areas.length" id="reservation-group-unplaced">
              {{ $t('reservations.groups.form.unplaced') }}
            </h3>
            <div class="table-options">
              <button
                v-for="table in unplaced"
                :key="table.id"
                type="button"
                class="table-option"
                :class="{
                  selected: tableIds.includes(table.id),
                  inactive: !table.enabled,
                }"
                :disabled="saving || (!tableIds.includes(table.id) && full)"
                :aria-pressed="tableIds.includes(table.id) ? 'true' : 'false'"
                @click="toggleTable(table.id)"
              >
                <PosUiIcon
                  :name="
                    tableIds.includes(table.id)
                      ? 'check-circle'
                      : 'checkbox-blank-circle-outline'
                  "
                  :size="18"
                />
                <span class="option-text"
                  ><span>{{ table.name }}</span
                  ><span class="capacity"
                    >{{
                      $t('reservations.timeline.seats', {
                        capacity: table.capacity,
                      })
                    }}<span v-if="!table.enabled">
                      · {{ $t('reservations.tables.disabled') }}</span
                    ></span
                  ></span
                >
              </button>
            </div>
          </div>
        </div>
      </PosUiField>
      <PosUiField
        :label="$t('reservations.groups.form.name')"
        input-id="reservation-group-name"
        :error="
          submitted && !name.trim()
            ? $t('reservations.shifts.form.nameRequired')
            : undefined
        "
        required
        ><PosUiInput
          id="reservation-group-name"
          :value="name"
          :maxlength="64"
          :disabled="saving"
          @input="setName"
      /></PosUiField>
      <div class="range-fields">
        <PosUiField
          :label="$t('reservations.groups.form.minParty')"
          input-id="reservation-group-min"
          required
          ><PosUiInput
            id="reservation-group-min"
            :value="minParty"
            type="number"
            min="1"
            max="200"
            step="1"
            inputmode="numeric"
            :disabled="saving"
            @input="setRange('min', $event)" /></PosUiField
        ><PosUiField
          :label="$t('reservations.groups.form.maxParty')"
          input-id="reservation-group-max"
          required
          ><PosUiInput
            id="reservation-group-max"
            :value="maxParty"
            type="number"
            min="1"
            max="200"
            step="1"
            inputmode="numeric"
            :disabled="saving"
            @input="setRange('max', $event)"
        /></PosUiField>
      </div>
      <p v-if="submitted && !rangeValid" class="error" role="alert">
        {{ $t('reservations.groups.form.rangeInvalid') }}
      </p>
      <p v-if="error" class="error" role="alert">{{ error }}</p>
    </div>
    <footer class="modal-actions">
      <PosUiButton
        color="neutral"
        variant="outline"
        :disabled="saving"
        @click="modals.hide(modalName)"
        >{{ $t('cancel') }}</PosUiButton
      ><PosUiButton type="submit" :disabled="saving">{{
        $t('save')
      }}</PosUiButton>
    </footer>
  </form>
</template>

<style scoped lang="scss">
  .reservation-group-modal {
    @apply tw-flex tw-min-h-0 tw-flex-col tw-bg-white;
    max-height: 90dvh;
    .modal-header {
      @apply tw-border-b tw-border-neutral-200 tw-px-5 tw-py-4;
      h2 {
        @apply tw-m-0 tw-text-lg tw-font-semibold tw-text-neutral-900;
      }
      p {
        @apply tw-m-0 tw-mt-0.5 tw-text-sm tw-text-neutral-500;
      }
    }
    .modal-content {
      @apply tw-flex tw-min-h-0 tw-flex-col tw-gap-4 tw-overflow-y-auto tw-p-5;
      // The plan sizes its cells from this width: a scrollbar that comes and
      // goes with the content height would otherwise resize it in a loop.
      scrollbar-gutter: stable;
    }
    .modal-actions {
      @apply tw-flex tw-flex-none tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-200 tw-p-4;
    }
    .plan-loading {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-canvas tw-p-2;
      .plan-skeleton {
        @apply tw-h-[280px] tw-w-full tw-rounded-lg;
      }
    }
    .plan-error {
      @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-red-200 tw-bg-red-50 tw-p-3 tw-text-sm tw-text-red-700;
    }
    .area-switch {
      @apply tw-mb-0;
      // Long sector names wrap instead of widening the modal.
      :deep(.pos-ui-segmented-option) {
        @apply tw-h-auto tw-min-h-11 tw-min-w-0 tw-whitespace-normal tw-px-2 tw-py-2;
      }
    }
    .tables-picker {
      @apply tw-flex tw-flex-col tw-gap-3;
    }
    .unplaced {
      @apply tw-flex tw-flex-col tw-gap-2;
      &.first {
        order: -1;
      }
      h3 {
        @apply tw-m-0 tw-text-xs tw-font-medium tw-text-neutral-500;
      }
    }
    .table-options {
      @apply tw-grid tw-grid-cols-2 tw-gap-2;
    }
    .table-option {
      @apply tw-flex tw-min-h-14 tw-min-w-0 tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-neutral-800 hover:tw-border-neutral-400 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-orange-500/40;
      .pos-ui-icon {
        @apply tw-shrink-0 tw-text-neutral-400;
      }
      &.selected {
        @apply tw-border-orange-600 tw-bg-orange-50 tw-text-orange-900;
        .pos-ui-icon {
          @apply tw-text-orange-600;
        }
      }
      &.inactive {
        @apply tw-opacity-60;
      }
      &:disabled:not(.selected) {
        @apply tw-cursor-not-allowed tw-opacity-40;
      }
    }
    .option-text {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5 tw-break-words;
    }
    .capacity {
      @apply tw-text-xs tw-text-neutral-500;
    }
    // What the combination holds right now, whichever area is on screen.
    .selection {
      @apply tw-rounded-lg tw-bg-canvas tw-p-3;
      h3 {
        @apply tw-m-0 tw-mb-2 tw-text-xs tw-font-medium tw-text-neutral-500;
      }
      .selection-seats {
        @apply tw-font-normal tw-tabular-nums;
      }
      .selection-limit {
        @apply tw-ml-2 tw-rounded-full tw-bg-neutral-200 tw-px-2 tw-py-0.5 tw-font-normal tw-text-neutral-700;
      }
      .selection-warning {
        @apply tw-m-0 tw-mt-2 tw-rounded-lg tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-sm tw-text-amber-800;
      }
      .selection-empty {
        @apply tw-m-0 tw-text-sm tw-text-neutral-500;
      }
      .selection-list {
        @apply tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-2 tw-p-0;
      }
      .selection-chip {
        @apply tw-inline-flex tw-max-w-full tw-min-h-9 tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-pl-3 tw-pr-1 tw-text-sm tw-font-medium tw-text-neutral-800;
        > span:first-child {
          @apply tw-min-w-0 tw-truncate;
        }
      }
      .selection-area {
        @apply tw-font-normal tw-text-neutral-500;
      }
      // A 44px hit area that does not stretch the chip.
      .selection-remove {
        @apply tw--my-2 tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-neutral-500 hover:tw-text-neutral-900 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-orange-500/40;
      }
    }
    .range-fields {
      @apply tw-grid tw-grid-cols-2 tw-gap-3;
    }
    .error {
      @apply tw-m-0 tw-text-sm tw-text-red-600;
    }
  }
</style>
